var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Button_1;
import React, { createElement, forwardRef, isValidElement, PureComponent } from 'react';
import { ButtonTheme, ButtonType, Placement, Size } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, color, element, eventListener, expression, func, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, defaultClasses, deprecationWarningMessage, enumValues, isEnumValid, isFunction, resolveColor, trigger } from '@philips-internal/oswald-ui-utils';
import { InlineImage } from '../inline-image/inline-image.js';
import { Loader } from '../loader/loader.js';
import { ButtonAPI } from './button-api.js';
import './button.css';
let Button = Button_1 = class Button extends PureComponent {
    constructor() {
        super(...arguments);
        this.onClick = (event) => {
            const { loading, onClick, preventDefault, stopPropagation } = this.props;
            if (loading) {
                return;
            }
            if (preventDefault) {
                event.preventDefault();
            }
            if (stopPropagation) {
                event.stopPropagation();
            }
            if (isFunction(onClick)) {
                onClick(new ClickEvent({
                    preventDefault: () => event.preventDefault(),
                    stopPropagation: () => event.stopPropagation(),
                }));
            }
        };
    }
    componentDidMount() {
        if (this.props.theme === ButtonTheme.DEFAULT) {
            deprecationWarningMessage({
                deadlineVersion: '7.0.0',
                feature: true,
                replaced: 'ButtonTheme.DEFAULT',
                scope: 'Button',
                startingVersion: '6.10.1',
            });
        }
    }
    getButtonClasses() {
        const { bold, className, enabled = Button_1.defaultProps.enabled, fitParent, loading = false, noPadding, size, theme, iconPlacement } = this.props;
        return classNames({
            'c-button': true,
            'c-button--fit-parent': Boolean(fitParent),
            'c-button--no-padding': Boolean(noPadding),
            [`c-button--${size}`]: isEnumValid(Size, size),
            [`c-button--${theme}`]: isEnumValid(ButtonTheme, theme),
            [`c-button--icon-${iconPlacement}`]: isEnumValid(Placement, iconPlacement),
            'u-text-bold': Boolean(bold),
            ...defaultClasses({ enabled, loading }),
            [`${className}`]: Boolean(className),
        });
    }
    getButtonStyle() {
        const { background, color } = this.props;
        return {
            ...resolveColor(color),
            background,
        };
    }
    renderIcon() {
        const { icon, iconClassName, loading } = this.props;
        if (loading) {
            return (React.createElement(Loader, { className: "c-button__loader", showMessage: false, size: 24 }));
        }
        if (!icon) {
            return null;
        }
        if (isValidElement(icon)) {
            const oldClassName = icon.props.className ?? '';
            const newClassName = `c-button__icon ${oldClassName}`;
            return createElement(icon.type, { ...icon.props, className: newClassName });
        }
        const className = classNames({
            'c-button__icon': true,
            [`${iconClassName}`]: Boolean(iconClassName),
        });
        return React.createElement(InlineImage, { className: className, src: icon });
    }
    renderText() {
        const { loading, value = Button_1.defaultProps.value } = this.props;
        if (loading) {
            return null;
        }
        return MessageService.translate(value);
    }
    render() {
        const { badge, enabled, innerRef, onBlur, onContextMenuClick, onDoubleClick, onFocus, type, visible } = this.props;
        if (!visible) {
            return null;
        }
        return (React.createElement("button", { className: this.getButtonClasses(), "data-badge": badge, disabled: !enabled, onBlur: trigger(onBlur), onClick: (event) => this.onClick(event), onContextMenu: trigger(onContextMenuClick), onDoubleClick: trigger(onDoubleClick), onFocus: trigger(onFocus), ref: innerRef, style: this.getButtonStyle(), type: type, "x-contrast": "bg" },
            this.renderIcon(),
            React.createElement("span", { className: "c-button__text u-hide@empty", "x-contrast": "cl" }, this.renderText())));
    }
};
Button.propTypes = {
    background: string,
    badge: string,
    bold: bool,
    className: string,
    color: color,
    enabled: bool,
    fitParent: bool,
    icon: oneOfType([string, element]),
    iconClassName: string,
    iconPlacement: oneOf([Placement.LEFT, Placement.RIGHT]),
    innerRef: func.notEnumerable(),
    loading: bool,
    noPadding: bool.notEnumerable(),
    onBlur: eventListener,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onFocus: eventListener,
    preventDefault: bool.group('events'),
    size: oneOf([Size.NONE, Size.SMALL, Size.LARGE]),
    stopPropagation: bool.group('events'),
    theme: oneOf(enumValues(ButtonTheme)),
    type: oneOf(enumValues(ButtonType)),
    value: expression,
    visible: bool,
};
Button.defaultProps = {
    enabled: true,
    fitParent: false,
    iconPlacement: Placement.LEFT,
    noPadding: false,
    theme: ButtonTheme.PRIMARY,
    type: ButtonType.BUTTON,
    value: 'Button',
    visible: true,
};
Button = Button_1 = __decorate([
    AwesomeComponent({ api: ButtonAPI, type: ComponentType.NAVIGATION })
], Button);
const buttonWithRef = forwardRef((props, ref) => React.createElement(Button, { ...props, innerRef: ref }));
buttonWithRef.displayName = Button.name;
export { buttonWithRef as Button, };
//# sourceMappingURL=button.js.map